import { TimeUnit } from "@archetypes/form";
import { format } from "date-fns";

type NumericCollection = unknown[] | { toArray: () => unknown };

function normaliseNumberArray(
  value: NumericCollection,
  functionName: string,
  options?: { allowEmpty?: boolean },
): number[] {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === "object" && value !== null && "toArray" in value
      ? value.toArray()
      : null;

  if (!Array.isArray(rawValues)) {
    throw new Error(`${functionName} expects an array of numbers`);
  }

  if (!options?.allowEmpty && rawValues.length === 0) {
    throw new Error(`${functionName} expects a non-empty array of numbers`);
  }

  const normalisedValues = rawValues.map((item) => Number(item));

  if (!normalisedValues.every((item) => Number.isFinite(item))) {
    throw new Error(`${functionName} expects an array of numbers`);
  }

  return normalisedValues;
}

export function DATEDIF(date1: Date, date2: Date, format: TimeUnit): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
    throw new Error("Invalid date");
  }
  const MS_IN_SECOND = 1000;
  const MS_IN_MINUTE = MS_IN_SECOND * 60;
  const MS_IN_HOUR = MS_IN_MINUTE * 60;
  const MS_IN_DAY = MS_IN_HOUR * 24;
  const MS_IN_WEEK = MS_IN_DAY * 7;

  const diffMilliseconds = d2.getTime() - d1.getTime();
  const updatedFormat = format?.toString().toLowerCase();
  switch (updatedFormat) {
    case TimeUnit.Seconds:
      return Math.floor(diffMilliseconds / MS_IN_SECOND);

    case TimeUnit.Minutes:
      return Math.floor(diffMilliseconds / MS_IN_MINUTE);

    case TimeUnit.Hours:
      return Math.floor(diffMilliseconds / MS_IN_HOUR);

    case TimeUnit.Days:
      return Math.floor(diffMilliseconds / MS_IN_DAY);

    case TimeUnit.Week:
      return Math.floor(diffMilliseconds / MS_IN_WEEK);

    case TimeUnit.Months: {
      const yearDiff = d2.getFullYear() - d1.getFullYear();
      const monthDiff = d2.getMonth() - d1.getMonth();
      return yearDiff * 12 + monthDiff;
    }

    case TimeUnit.Year:
      return d2.getFullYear() - d1.getFullYear();

    default:
      throw new Error(`Invalid format: ${format}`);
  }
}

export function PERCENT(percentage: number, baseValue: number): number {
  return (percentage / 100) * baseValue;
}

export function DATEFORMAT(date: string, dateFormat: string): string | number {
  const dt = new Date(date);
  if (isNaN(dt.getTime())) {
    throw new Error("Invalid date");
  }
  return format(dt, dateFormat);
}

export function STATISTICAL_SUM(values: NumericCollection): number {
  return normaliseNumberArray(values, "STATISTICAL_SUM", { allowEmpty: true }).reduce(
    (total, value) => total + value,
    0,
  );
}

export function STATISTICAL_MEAN(values: NumericCollection): number {
  const items = normaliseNumberArray(values, "STATISTICAL_MEAN");
  return STATISTICAL_SUM(items) / items.length;
}

export function STATISTICAL_MEDIAN(values: NumericCollection): number {
  const items = [...normaliseNumberArray(values, "STATISTICAL_MEDIAN")].sort((a, b) => a - b);
  const middleIndex = Math.floor(items.length / 2);

  if (items.length % 2 === 0) {
    return (items[middleIndex - 1] + items[middleIndex]) / 2;
  }

  return items[middleIndex];
}

export function STATISTICAL_MODE(values: NumericCollection): number {
  const items = normaliseNumberArray(values, "STATISTICAL_MODE");
  const frequencyMap = new Map<number, number>();
  let maxFrequency = 0;

  items.forEach((value) => {
    const nextFrequency = (frequencyMap.get(value) ?? 0) + 1;
    frequencyMap.set(value, nextFrequency);
    maxFrequency = Math.max(maxFrequency, nextFrequency);
  });

  const modes = Array.from(frequencyMap.entries())
    .filter(([, frequency]) => frequency === maxFrequency)
    .map(([value]) => value)
    .sort((a, b) => a - b);

  return modes[0];
}
